import axios from 'axios'
import produce from 'immer'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react'

import Cookies from 'js-cookie'
import { apiURI, constants } from 'src/lib/utils/constants'

import { initialOverviewFilterData } from 'src/assets/AnalyticsConfig'
import { isComparisonRangeWithin24h, isCurrentRangeWithin24h } from 'src/components/sso-analytics/charts/utils'
import { InitalStateWithComparison, useDateFilters } from 'src/lib/hooks/useDateFilters'
import { FilterConfigType, FilterDataType } from 'src/lib/types/filters'
import { extractDateFromFilters } from '../../utils/analyticsHelpers'
import { getAnalyticsConfig } from './helpers'
import { AnalyticsState, TimeScale, WidgetConfig, WidgetId, WidgetSections, Widgets } from './types'
import { useUser } from '../UserContext'

const initialState: AnalyticsState = {
  isLoading: true,
  widgetConfig: {
    [WidgetSections.summary]: [],
    [WidgetSections.conversion]: [],
    [WidgetSections.payments]: [],
    [WidgetSections.orders]: [],
    [WidgetSections.discounts]: [],
    [WidgetSections.rto]: [],
    [WidgetSections.upsells]: [],
    [WidgetSections.rewards]: []
  }
}

type AnalyticsAction =
  | {
      type: 'SET_WIDGET_CONFIG'
      payload: {
        widgetConfig: Widgets
      }
    }
  | {
      type: 'SET_IS_LOADING'
      payload: { isLoading: boolean }
    }

function produceCallback(draft: AnalyticsState, action: AnalyticsAction) {
  switch (action.type) {
    case 'SET_WIDGET_CONFIG': {
      draft.widgetConfig = action.payload.widgetConfig.widgetConfig
      break
    }
    case 'SET_IS_LOADING': {
      draft.isLoading = action.payload.isLoading
      break
    }
  }
}

type AnalyticsFilters = {
  channel?: string[]
  timeRange: {
    start: string
    end: string
  }
  timeScale: TimeScale
  comparisonTimeRange?: {
    start: string
    end: string
  }
}

type AnalyticsContextType = {
  analytics: AnalyticsState
  actions: {
    setDateFilter: (dateFilter: FilterConfigType) => void
    setChannelData: (channelData: FilterDataType[]) => void
    handleDeleteColumnValue: (columnValue: string, filterIndex: number) => void
    setRefreshKey: (refreshKey: string) => void
    setAlertDialogState: (isOpen: boolean, widgetId?: string, initialThreshold?: number | string) => void
  }
  getters: {
    widgetConfig: {
      [key in WidgetSections]: {
        [key in WidgetId]: WidgetConfig
      }
    }
    filters: AnalyticsFilters
    dateFilter: FilterConfigType
    channelData: FilterDataType[]
    selectedTimeScale: TimeScale
    refreshKey: string
  }
  alertDialog: {
    isOpen: boolean
    widgetId: string | null
    initialThreshold?: number | string
  }
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined)

const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  const [refreshKey, setRefreshKey] = useState('wow')
  const [channelData, setChannelData] = useState<FilterDataType[]>(initialOverviewFilterData)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean
    widgetId: string | null
    initialThreshold?: number | string
  }>({ isOpen: false, widgetId: null })

  const { dateFilter, setDateFilterCtx } = useDateFilters('analytics_v2', InitalStateWithComparison)
  const { user } = useUser()

  const [analytics, dispatch] = useReducer(produce(produceCallback), initialState)

  useEffect(() => {
    if (!user) return
    setRefreshKey(`analytics-user-${user.id || 'anonymous'}-${Date.now()}`)

    const fetchAnalyticsConfiguraiton = async () => {
      try {
        setIsLoading(true)
        const response = await axios.get('/dashboard/widget/list', {
          baseURL: apiURI.CHECKOUT_NODE_V2,
          headers: { Authorization: Cookies.get(constants.AUTH_COOKIE_CLIENT) ?? '' }
        })
        dispatch({ type: 'SET_WIDGET_CONFIG', payload: { widgetConfig: response.data.data } })
      } catch (err) {
        if (err instanceof Error) {
          setError(err)
        } else {
          setError(new Error('Unknown error'))
        }
      } finally {
        setIsLoading(false)
      }
    }
    fetchAnalyticsConfiguraiton()
  }, [user])

  const selectedTimeScale: TimeScale = useMemo(() => {
    if (!dateFilter) return 'hourly'
    if (dateFilter.comparison === 'none') {
      return isCurrentRangeWithin24h(dateFilter) ? 'hourly' : 'daily'
    }
    return isCurrentRangeWithin24h(dateFilter) && isComparisonRangeWithin24h(dateFilter) ? 'hourly' : 'daily'
  }, [dateFilter])

  const filters = useMemo(() => {
    const _filters: AnalyticsFilters = {
      timeRange: {
        start: extractDateFromFilters(dateFilter, 'startDate'),
        end: extractDateFromFilters(dateFilter, 'endDate')
      },
      timeScale: selectedTimeScale
    }
    // Only add channel if there are values
    if (channelData[0].values.length > 0) {
      _filters.channel = channelData[0].values.map(channel => channel.id)
    }

    if (dateFilter.comparison !== 'none') {
      _filters.comparisonTimeRange = {
        start: extractDateFromFilters(dateFilter, 'compareToStartDate'),
        end: extractDateFromFilters(dateFilter, 'compareToEndDate')
      }
    }
    return _filters
  }, [dateFilter, selectedTimeScale, channelData])

  const handleDeleteColumnValue = useCallback((columnValue: string, filterIndex: number) => {
    setChannelData(
      produce((draft: any) => {
        const filter = draft[filterIndex]
        const deletedValueIndex = filter.values.findIndex((value: any) => value?.id === columnValue)
        if (deletedValueIndex !== -1) {
          filter.values.splice(deletedValueIndex, 1)
        }
      })
    )
  }, [])

  const setAlertDialogState = useCallback((isOpen: boolean, widgetId?: string, initialThreshold?: number | string) => {
    setAlertDialog(
      isOpen ? { isOpen: true, widgetId: widgetId ?? null, initialThreshold } : { isOpen: false, widgetId: null }
    )
  }, [])

  useEffect(() => {
    dispatch({ type: 'SET_IS_LOADING', payload: { isLoading } })
  }, [isLoading])

  const actions = useMemo(() => {
    return {
      setDateFilter: setDateFilterCtx,
      setChannelData,
      handleDeleteColumnValue,
      setRefreshKey,
      setAlertDialogState
    }
  }, [setDateFilterCtx, setChannelData, handleDeleteColumnValue, setRefreshKey, setAlertDialogState])

  const getters = useMemo(() => {
    return {
      widgetConfig: getAnalyticsConfig(analytics.widgetConfig),
      filters,
      dateFilter,
      channelData,
      selectedTimeScale,
      refreshKey
    }
  }, [analytics.widgetConfig, filters, dateFilter, channelData, selectedTimeScale, refreshKey])

  // TODO(shradhan): handle this
  if (error) {
    return <div>Error</div>
  }

  return (
    <AnalyticsContext.Provider value={{ analytics, actions, getters, alertDialog }}>
      {children}
    </AnalyticsContext.Provider>
  )
}

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext)
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider')
  }
  return context
}

export default AnalyticsProvider
